/**
 * Contacts & Leads Management
 * Comprehensive contact management for merchants, customers, leads, and waitlist entries
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, UserPlus, Mail, Phone, Tag, Target, Zap, Eye, Search, Filter,
  Download, Trash2, Plus, X, ChevronLeft, ChevronRight, CheckCircle,
  AlertTriangle, XCircle, Clock, TrendingUp, Globe, BarChart3,
  ArrowLeft, ExternalLink, MousePointer, Send, Activity, Calendar,
  Settings
} from 'lucide-react';
import SendCampaignModal from './SendCampaignModal';
import './ContactsManagement.css';

function ContactsManagement({ apiWithAccessControl }) {
  // Views
  const [activeView, setActiveView] = useState('overview');

  // Data
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [contactSegments, setContactSegments] = useState([]);
  const [contactCampaigns, setContactCampaigns] = useState([]);
  const [contactAutomations, setContactAutomations] = useState([]);
  const [contactActivity, setContactActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // List filters
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [typeCounts, setTypeCounts] = useState({ all: 0, merchants: 0, customers: 0, leads: 0, waitlist: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    contactType: 'ALL',
    status: '',
    tags: [],
    tagLogic: 'OR',
    segmentId: '',
    source: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Bulk selection
  const [selectedContacts, setSelectedContacts] = useState([]);

  // CRM tabs
  const [crmTab, setCrmTab] = useState('ALL'); // 'ALL' | 'CONTACTS' | 'LEADS'

  // Send Campaign
  const [showSendCampaign, setShowSendCampaign] = useState(false);
  const [sendCampaignContacts, setSendCampaignContacts] = useState([]);

  // Modals
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('ADD_TAGS');

  // Reference data
  const [allTags, setAllTags] = useState([]);
  const [allSegments, setAllSegments] = useState([]);

  // Lead/Waitlist status configs (fetched from API)
  const [leadStatusConfigs, setLeadStatusConfigs] = useState([]);
  const [waitlistStatusConfigs, setWaitlistStatusConfigs] = useState([]);

  // Status banner
  const [statusMessage, setStatusMessage] = useState(null);
  const statusTimerRef = useRef(null);

  // Detail tab
  const [detailTab, setDetailTab] = useState('campaigns');
  const [crmSyncingId, setCrmSyncingId] = useState(null);

  // Detail inline tag add
  const [showAddTagInput, setShowAddTagInput] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);

  // Search debounce
  const searchTimerRef = useRef(null);

  // ─── Status Banner ────────────────────────────────────────────────

  const showStatus = useCallback((type, text) => {
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
    }
    setStatusMessage({ type, text });
    statusTimerRef.current = setTimeout(() => {
      setStatusMessage(null);
      statusTimerRef.current = null;
    }, 3000);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Load lead/waitlist status configs from API
  useEffect(() => {
    loadStatusConfigs();
  }, []);

  const loadStatusConfigs = async () => {
    try {
      const [leadRes, waitlistRes] = await Promise.all([
        apiWithAccessControl('/admin/lead-statuses?category=LEAD&active=true'),
        apiWithAccessControl('/admin/lead-statuses?category=WAITLIST&active=true')
      ]);
      const leadData = await leadRes.json();
      const waitlistData = await waitlistRes.json();
      if (leadData.success) setLeadStatusConfigs(leadData.statuses || []);
      if (waitlistData.success) setWaitlistStatusConfigs(waitlistData.statuses || []);
    } catch (err) {
      console.error('Error loading status configs:', err);
    }
  };

  // ─── Data Loading ─────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl('/admin/contacts/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        showStatus('error', data.message || 'Failed to load contact stats');
      }
    } catch (err) {
      showStatus('error', 'Error loading contact statistics');
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl, showStatus]);

  const loadContacts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', pagination.limit);
      if (searchQuery) params.set('search', searchQuery);
      if (filters.contactType !== 'ALL') params.set('contactType', filters.contactType);
      if (filters.status) params.set('status', filters.status);
      if (filters.tags.length > 0) {
        params.set('tags', filters.tags.join(','));
        params.set('tagLogic', filters.tagLogic);
      }
      if (filters.segmentId) params.set('segmentId', filters.segmentId);
      if (filters.source) params.set('source', filters.source);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      params.set('sortBy', filters.sortBy);
      params.set('sortOrder', filters.sortOrder);

      const response = await apiWithAccessControl(`/admin/contacts?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts || []);
        setPagination(prev => ({
          ...prev,
          page: data.pagination.page,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
        setTypeCounts(data.counts || { all: 0, merchants: 0, customers: 0, leads: 0, waitlist: 0 });
      } else {
        showStatus('error', data.message || 'Failed to load contacts');
      }
    } catch (err) {
      showStatus('error', 'Error loading contacts');
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl, showStatus, searchQuery, filters, pagination.limit]);

  const loadContactDetail = useCallback(async (id, contactType) => {
    setLoading(true);
    setDetailTab('campaigns');
    try {
      const response = await apiWithAccessControl(`/admin/contacts/${id}?contactType=${contactType}`);
      const data = await response.json();
      if (data.success) {
        setContactDetail(data.contact);
        setContactSegments(data.segments || []);
        setContactCampaigns(data.campaigns || []);
        setContactAutomations(data.automations || []);
        setActiveView('detail');
        loadContactActivity(id, contactType);
      } else {
        showStatus('error', data.message || 'Failed to load contact details');
      }
    } catch (err) {
      showStatus('error', 'Error loading contact details');
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl, showStatus]);

  const loadContactActivity = useCallback(async (id, contactType) => {
    try {
      const response = await apiWithAccessControl(`/admin/contacts/${id}/activity?contactType=${contactType}`);
      const data = await response.json();
      if (data.success) {
        setContactActivity(data.activity || []);
      }
    } catch (err) {
      console.error('Error loading contact activity:', err);
    }
  }, [apiWithAccessControl]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [tagsRes, segmentsRes] = await Promise.all([
        apiWithAccessControl('/admin/tags?type=user'),
        apiWithAccessControl('/admin/segments')
      ]);
      const tagsData = await tagsRes.json();
      const segmentsData = await segmentsRes.json();

      if (tagsData && (tagsData.tags || Array.isArray(tagsData))) {
        const tagsList = tagsData.tags || tagsData;
        setAllTags(Array.isArray(tagsList) ? tagsList : []);
      }
      if (segmentsData && segmentsData.success) {
        setAllSegments(segmentsData.segments || []);
      }
    } catch (err) {
      console.error('Error loading reference data:', err);
    }
  }, [apiWithAccessControl]);

  // ─── Effects ──────────────────────────────────────────────────────

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    if (activeView === 'overview') {
      loadStats();
    }
  }, [activeView, loadStats]);

  useEffect(() => {
    if (activeView === 'list') {
      loadContacts(pagination.page);
    }
  }, [activeView, filters, searchQuery]);

  // ─── Search ───────────────────────────────────────────────────────

  const handleSearch = useCallback((value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchQuery(value);
    searchTimerRef.current = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
  }, []);

  // ─── Selection ────────────────────────────────────────────────────

  const toggleContactSelection = useCallback((id, contactType) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === id && c.contactType === contactType);
      if (exists) {
        return prev.filter(c => !(c.id === id && c.contactType === contactType));
      }
      return [...prev, { id, contactType }];
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    const allOnPage = contacts.map(c => ({ id: c.id, contactType: c.contactType }));
    const allSelected = allOnPage.every(cp =>
      selectedContacts.some(sc => sc.id === cp.id && sc.contactType === cp.contactType)
    );
    if (allSelected) {
      setSelectedContacts(prev =>
        prev.filter(sc => !allOnPage.some(cp => cp.id === sc.id && cp.contactType === sc.contactType))
      );
    } else {
      setSelectedContacts(prev => {
        const newSelection = [...prev];
        allOnPage.forEach(cp => {
          if (!newSelection.some(sc => sc.id === cp.id && sc.contactType === cp.contactType)) {
            newSelection.push(cp);
          }
        });
        return newSelection;
      });
    }
  }, [contacts, selectedContacts]);

  // ─── Bulk Actions ─────────────────────────────────────────────────

  const executeBulkAction = useCallback(async (action, payload) => {
    try {
      const contactIds = selectedContacts.length > 0
        ? selectedContacts
        : (contactDetail ? [{ id: contactDetail.id, contactType: contactDetail.contactType }] : []);

      if (contactIds.length === 0) {
        showStatus('error', 'No contacts selected');
        return;
      }

      const response = await apiWithAccessControl('/admin/contacts/bulk-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactIds, action, payload })
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', data.message || `${action.replace(/_/g, ' ')} completed successfully`);
        setShowBulkTagModal(false);
        setShowSegmentModal(false);
        setShowDeleteModal(false);
        setSelectedContacts([]);

        if (activeView === 'list') {
          loadContacts(pagination.page);
        } else if (activeView === 'detail' && contactDetail) {
          loadContactDetail(contactDetail.id, contactDetail.contactType);
        }
      } else {
        showStatus('error', data.message || `${action.replace(/_/g, ' ')} failed`);
      }
    } catch (err) {
      showStatus('error', `Error performing ${action.replace(/_/g, ' ').toLowerCase()}`);
    }
  }, [selectedContacts, contactDetail, apiWithAccessControl, showStatus, activeView, pagination.page, loadContacts, loadContactDetail]);

  // ─── Export ───────────────────────────────────────────────────────

  const exportContacts = useCallback(async (scope) => {
    try {
      let dataToExport = [];

      if (scope === 'selected') {
        dataToExport = contacts.filter(c =>
          selectedContacts.some(sc => sc.id === c.id && sc.contactType === c.contactType)
        );
      } else {
        const params = new URLSearchParams();
        params.set('page', 1);
        // Match the backend's export-mode ceiling (see contactManagementPrisma.js
        // — anything >= 1000 flips the endpoint into export mode which fetches
        // every matching row per model instead of the 2× merge heuristic).
        params.set('limit', 50000);
        if (searchQuery) params.set('search', searchQuery);
        if (filters.contactType !== 'ALL') params.set('contactType', filters.contactType);
        if (filters.status) params.set('status', filters.status);
        if (filters.tags.length > 0) {
          params.set('tags', filters.tags.join(','));
          params.set('tagLogic', filters.tagLogic);
        }
        if (filters.segmentId) params.set('segmentId', filters.segmentId);
        if (filters.source) params.set('source', filters.source);
        if (filters.startDate) params.set('startDate', filters.startDate);
        if (filters.endDate) params.set('endDate', filters.endDate);
        params.set('sortBy', filters.sortBy);
        params.set('sortOrder', filters.sortOrder);

        const response = await apiWithAccessControl(`/admin/contacts?${params.toString()}`);
        const data = await response.json();
        if (data.success) {
          dataToExport = data.contacts || [];
          // Warn the admin if the backend truncated — better to know
          // your CSV is missing rows than to discover it in Excel later.
          if (data.pagination?.truncated) {
            showStatus(
              'warning',
              `Export contains the first ${data.pagination.hardCeiling.toLocaleString()} contacts of ${data.pagination.total.toLocaleString()} total. Narrow your filters to export the rest.`
            );
          }
        }
      }

      if (dataToExport.length === 0) {
        showStatus('error', 'No contacts to export');
        return;
      }

      const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csvValue = (value, fallback = '') => {
        if (value === null || value === undefined || value === '') return fallback;
        return value;
      };
      const headers = [
        'Name',
        'Email',
        'Phone',
        'Type',
        'Merchant Code',
        'Merchant Tier',
        'KYC Level',
        'KYC Status',
        'KYC Progress',
        'Store Name',
        'Store Status',
        'Store Link',
        'Tags',
        'Status',
        'Source',
        'Joined'
      ];
      const rows = dataToExport.map(c => [
        csvCell(c.name),
        csvCell(c.email),
        csvCell(c.phone),
        c.contactType || '',
        csvCell(csvValue(c.meta?.merchantCode, 'N/A')),
        csvCell(csvValue(c.meta?.merchantTier, 'N/A')),
        csvCell(csvValue(c.meta?.kycLevel, 'N/A')),
        csvCell(csvValue(c.meta?.kycStatus, 'N/A')),
        csvCell(c.meta?.kycProgress !== undefined && c.meta?.kycProgress !== null ? `${c.meta.kycProgress}%` : 'N/A'),
        csvCell(csvValue(c.meta?.storeName, 'N/A')),
        csvCell(csvValue(c.meta?.storeStatus, 'N/A')),
        csvCell(csvValue(c.meta?.storeLink, 'N/A')),
        csvCell((c.tags || []).join(', ')),
        c.status || '',
        c.source || '',
        c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''
      ]);

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showStatus('success', `Exported ${dataToExport.length} contacts`);
      setShowExportModal(false);
    } catch (err) {
      showStatus('error', 'Error exporting contacts');
    }
  }, [contacts, selectedContacts, searchQuery, filters, apiWithAccessControl, showStatus]);

  // ─── Pagination ───────────────────────────────────────────────────

  const getPageNumbers = useCallback(() => {
    const { page, totalPages } = pagination;
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [pagination]);

  const goToPage = useCallback((page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page }));
    loadContacts(page);
  }, [pagination.totalPages, loadContacts]);

  // ─── Filter Helpers ───────────────────────────────────────────────

  const clearFilters = useCallback(() => {
    setFilters({
      contactType: 'ALL',
      status: '',
      tags: [],
      tagLogic: 'OR',
      segmentId: '',
      source: '',
      startDate: '',
      endDate: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (filters.contactType !== 'ALL') count++;
    if (filters.status) count++;
    if (filters.tags.length > 0) count++;
    if (filters.segmentId) count++;
    if (filters.source) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.sortBy !== 'createdAt' || filters.sortOrder !== 'desc') count++;
    return count;
  }, [filters]);

  // ─── Send Campaign Handler ────────────────────────────────────────

  const handleSendCampaign = (contacts) => {
    setSendCampaignContacts(contacts);
    setShowSendCampaign(true);
  };

  const syncContactToCrm = useCallback(async (contact) => {
    if (!contact?.id || !contact?.contactType) return;
    const syncKey = `${contact.contactType}-${contact.id}`;
    setCrmSyncingId(syncKey);
    try {
      const response = await apiWithAccessControl('/admin/crm/sync-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          contactType: contact.contactType
        })
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', data.message || 'Contact synced to CRM');
      } else {
        showStatus('error', data.message || 'CRM sync failed');
      }
    } catch (err) {
      showStatus('error', 'Error syncing contact to CRM');
    } finally {
      setCrmSyncingId(null);
    }
  }, [apiWithAccessControl, showStatus]);

  // ─── Lead Status Labels (derived from API configs) ─────────────────────────

  const LEAD_STATUS_LABELS = { PENDING: 'Pending', INVITED: 'Invited', CONVERTED: 'Converted', UNSUBSCRIBED: 'Unsubscribed' };
  leadStatusConfigs.forEach(s => { LEAD_STATUS_LABELS[s.value] = s.label; });
  waitlistStatusConfigs.forEach(s => { LEAD_STATUS_LABELS[s.value] = s.label; });

  const LEAD_STATUSES = leadStatusConfigs.map(s => s.value);
  // Waitlist uses a fixed Prisma enum — always use these 4 values
  const FIXED_WAITLIST_STATUSES = ['PENDING', 'INVITED', 'CONVERTED', 'UNSUBSCRIBED'];
  const FIXED_WAITLIST_LABELS = { PENDING: 'Pending', INVITED: 'Invited', CONVERTED: 'Converted', UNSUBSCRIBED: 'Unsubscribed' };
  const WAITLIST_STATUSES = FIXED_WAITLIST_STATUSES;

  const getStatusColor = (statusValue) => {
    const config = [...leadStatusConfigs, ...waitlistStatusConfigs].find(s => s.value === statusValue);
    return config?.color || '#6b7280';
  };

  const updateLeadStatus = useCallback(async (id, contactType, status) => {
    try {
      const response = await apiWithAccessControl(`/admin/contacts/${id}/lead-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, contactType })
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', `Lead status updated to ${LEAD_STATUS_LABELS[status] || status}`);
        loadContactDetail(id, contactType);
      } else {
        showStatus('error', data.message || 'Failed to update lead status');
      }
    } catch (err) {
      showStatus('error', 'Error updating lead status');
    }
  }, [apiWithAccessControl, showStatus, loadContactDetail]);

  // ─── Helpers ──────────────────────────────────────────────────────

  const getInitials = (name) => {
    if (!name) return '??';
    return name.substring(0, 2).toUpperCase();
  };

  const avatarColor = (contactType) => {
    const colors = {
      MERCHANT: '#0f6b5e',
      CUSTOMER: '#1976d2',
      LEAD: '#f57c00',
      WAITLIST: '#7b1fa2'
    };
    return colors[contactType] || '#666';
  };

  const getStatusOptions = (contactType) => {
    switch (contactType) {
      case 'MERCHANT':
        return ['Active', 'Inactive', 'Opted Out'];
      case 'CUSTOMER':
        return ['Active', 'Inactive'];
      case 'LEAD':
        return ['New', 'Contacted', 'Qualified', 'Converted', 'Unsubscribed', 'Spam'];
      case 'WAITLIST':
        return ['Pending', 'Invited', 'Converted', 'Unsubscribed'];
      default:
        return ['Active', 'Inactive', 'Opted Out'];
    }
  };

  // ─── Status Banner Component ──────────────────────────────────────

  const StatusBanner = () => {
    if (!statusMessage) return null;
    return (
      <div className={`mkt-con-status-banner mkt-con-status-${statusMessage.type}`}>
        {statusMessage.type === 'success' ? (
          <CheckCircle size={18} />
        ) : (
          <AlertTriangle size={18} />
        )}
        <span>{statusMessage.text}</span>
        <button className="mkt-con-status-close" onClick={() => setStatusMessage(null)}>
          <X size={14} />
        </button>
      </div>
    );
  };

  // ─── VIEW: Overview ───────────────────────────────────────────────

  const ContactsOverview = () => {
    if (!stats) {
      return (
        <div className="mkt-con-loading">
          <div className="mkt-con-spinner" />
          <p>Loading contact statistics...</p>
        </div>
      );
    }

    return (
      <div className="mkt-con-overview">
        <div className="mkt-con-overview-header">
          <div>
            <h2 className="mkt-con-title">Contacts & Leads</h2>
            <p className="mkt-con-subtitle">Unified view of all contacts across your platform</p>
          </div>
          <button
            className="mkt-con-btn mkt-con-btn-primary"
            onClick={() => { setActiveView('list'); setPagination(prev => ({ ...prev, page: 1 })); }}
          >
            <Users size={16} />
            View All Contacts
          </button>
        </div>

        <div className="mkt-con-stats-grid">
          <div className="mkt-con-stat-card">
            <div className="mkt-con-stat-icon" style={{ background: '#e8f5e9' }}>
              <Users size={24} style={{ color: '#2e7d32' }} />
            </div>
            <div className="mkt-con-stat-info">
              <span className="mkt-con-stat-value">{(stats.totalContacts || 0).toLocaleString()}</span>
              <span className="mkt-con-stat-label">Total Contacts</span>
            </div>
          </div>
          <div className="mkt-con-stat-card">
            <div className="mkt-con-stat-icon" style={{ background: '#e0f2f1' }}>
              <Users size={24} style={{ color: '#0f6b5e' }} />
            </div>
            <div className="mkt-con-stat-info">
              <span className="mkt-con-stat-value">{(stats.byType?.merchants || 0).toLocaleString()}</span>
              <span className="mkt-con-stat-label">Merchants</span>
            </div>
          </div>
          <div className="mkt-con-stat-card">
            <div className="mkt-con-stat-icon" style={{ background: '#e3f2fd' }}>
              <UserPlus size={24} style={{ color: '#1976d2' }} />
            </div>
            <div className="mkt-con-stat-info">
              <span className="mkt-con-stat-value">{(stats.byType?.customers || 0).toLocaleString()}</span>
              <span className="mkt-con-stat-label">Customers</span>
            </div>
          </div>
          <div className="mkt-con-stat-card">
            <div className="mkt-con-stat-icon" style={{ background: '#fff3e0' }}>
              <MousePointer size={24} style={{ color: '#f57c00' }} />
            </div>
            <div className="mkt-con-stat-info">
              <span className="mkt-con-stat-value">{(stats.byType?.leads || 0).toLocaleString()}</span>
              <span className="mkt-con-stat-label">Leads</span>
            </div>
          </div>
          <div className="mkt-con-stat-card">
            <div className="mkt-con-stat-icon" style={{ background: '#f3e5f5' }}>
              <Clock size={24} style={{ color: '#7b1fa2' }} />
            </div>
            <div className="mkt-con-stat-info">
              <span className="mkt-con-stat-value">{(stats.byType?.waitlist || 0).toLocaleString()}</span>
              <span className="mkt-con-stat-label">Waitlist</span>
            </div>
          </div>
        </div>

        <div className="mkt-con-status-row">
          <div className="mkt-con-status-item">
            <CheckCircle size={16} className="mkt-con-icon-success" />
            <span className="mkt-con-status-count">{(stats.byStatus?.active || 0).toLocaleString()}</span>
            <span className="mkt-con-status-label">Active</span>
          </div>
          <div className="mkt-con-status-item">
            <XCircle size={16} className="mkt-con-icon-warning" />
            <span className="mkt-con-status-count">{(stats.byStatus?.optedOut || 0).toLocaleString()}</span>
            <span className="mkt-con-status-label">Opted Out</span>
          </div>
          <div className="mkt-con-status-item">
            <AlertTriangle size={16} className="mkt-con-icon-muted" />
            <span className="mkt-con-status-count">{(stats.byStatus?.unsubscribed || 0).toLocaleString()}</span>
            <span className="mkt-con-status-label">Unsubscribed</span>
          </div>
          <div className="mkt-con-status-item">
            <TrendingUp size={16} className="mkt-con-icon-info" />
            <span className="mkt-con-status-count">{(stats.byStatus?.converted || 0).toLocaleString()}</span>
            <span className="mkt-con-status-label">Converted</span>
          </div>
        </div>

        <div className="mkt-con-section">
          <h3 className="mkt-con-section-title">
            <Tag size={18} />
            Top Tags
          </h3>
          <div className="mkt-con-tags-list">
            {stats.topTags && stats.topTags.length > 0 ? (
              stats.topTags.map((t, idx) => (
                <span key={idx} className="mkt-con-tag-chip">
                  {t.tag}
                  <span className="mkt-con-tag-count">{t.count}</span>
                </span>
              ))
            ) : (
              <p className="mkt-con-empty-text">No tags assigned yet</p>
            )}
          </div>
        </div>

        <div className="mkt-con-section">
          <h3 className="mkt-con-section-title">
            <TrendingUp size={18} />
            Recent Signups
          </h3>
          <p className="mkt-con-signup-stats">
            Last 7 days: <strong>{stats.recentSignups?.last7Days || 0}</strong>
            {' | '}
            Last 30 days: <strong>{stats.recentSignups?.last30Days || 0}</strong>
          </p>
        </div>

        <div className="mkt-con-section">
          <h3 className="mkt-con-section-title">
            <Activity size={18} />
            Recent Contacts
          </h3>
          {stats.recentContacts && stats.recentContacts.length > 0 ? (
            <div className="mkt-con-recent-list">
              {stats.recentContacts.slice(0, 5).map((contact, idx) => (
                <div
                  key={idx}
                  className="mkt-con-recent-item"
                  onClick={() => loadContactDetail(contact.id, contact.contactType)}
                >
                  <div className="mkt-con-contact-cell">
                    <div className="mkt-con-avatar" style={{ background: avatarColor(contact.contactType) }}>
                      {getInitials(contact.name)}
                    </div>
                    <div className="mkt-con-contact-info">
                      <strong>{contact.name || 'Unknown'}</strong>
                      <small>{contact.email}</small>
                    </div>
                  </div>
                  <div className="mkt-con-recent-meta">
                    <span className={`mkt-con-type-badge mkt-con-type-${(contact.contactType || '').toLowerCase()}`}>
                      {contact.contactType}
                    </span>
                    <small>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : ''}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mkt-con-empty-text">No recent contacts</p>
          )}
        </div>

        <div className="mkt-con-info-grid">
          <div className="mkt-con-info-card">
            <div className="mkt-con-info-icon">
              <Globe size={24} />
            </div>
            <h4>Unified View</h4>
            <p>
              All your contacts from merchants, customers, leads, and waitlist entries are unified
              into a single searchable view. Filter by type, tags, segments, and more.
            </p>
          </div>
          <div className="mkt-con-info-card">
            <h4>Quick Actions</h4>
            <div className="mkt-con-quick-actions">
              <button
                className="mkt-con-btn mkt-con-btn-outline"
                onClick={() => { setActiveView('list'); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <Users size={16} />
                View Contacts
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-outline"
                onClick={() => {
                  setActiveView('list');
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setTimeout(() => setShowExportModal(true), 300);
                }}
              >
                <Download size={16} />
                Export All
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-outline"
                onClick={() => {
                  setActiveView('list');
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setTimeout(() => setShowFilters(true), 300);
                }}
              >
                <Tag size={16} />
                Manage Tags
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── VIEW: Contacts List ──────────────────────────────────────────

  const ContactsList = () => {
    const [filterTagInput, setFilterTagInput] = useState('');
    const [filterTagSuggestions, setFilterTagSuggestions] = useState([]);

    const activeFilterCount = getActiveFilterCount();

    const allOnPageSelected = contacts.length > 0 && contacts.every(c =>
      selectedContacts.some(sc => sc.id === c.id && sc.contactType === c.contactType)
    );

    const handleFilterTagInput = (value) => {
      setFilterTagInput(value);
      if (value.trim()) {
        const suggestions = allTags.filter(t =>
          (t.tag || t.name || '').toLowerCase().includes(value.toLowerCase()) &&
          !filters.tags.includes(t.tag || t.name || '')
        );
        setFilterTagSuggestions(suggestions);
      } else {
        setFilterTagSuggestions([]);
      }
    };

    const addFilterTag = (tagName) => {
      if (!filters.tags.includes(tagName)) {
        setFilters(prev => ({ ...prev, tags: [...prev.tags, tagName] }));
      }
      setFilterTagInput('');
      setFilterTagSuggestions([]);
    };

    const removeFilterTag = (tagName) => {
      setFilters(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagName) }));
    };

    const handleSortChange = (value) => {
      switch (value) {
        case 'newest':
          setFilters(prev => ({ ...prev, sortBy: 'createdAt', sortOrder: 'desc' }));
          break;
        case 'oldest':
          setFilters(prev => ({ ...prev, sortBy: 'createdAt', sortOrder: 'asc' }));
          break;
        case 'name':
          setFilters(prev => ({ ...prev, sortBy: 'name', sortOrder: 'asc' }));
          break;
        case 'email':
          setFilters(prev => ({ ...prev, sortBy: 'email', sortOrder: 'asc' }));
          break;
        default:
          break;
      }
    };

    const getSortValue = () => {
      if (filters.sortBy === 'createdAt' && filters.sortOrder === 'desc') return 'newest';
      if (filters.sortBy === 'createdAt' && filters.sortOrder === 'asc') return 'oldest';
      if (filters.sortBy === 'name') return 'name';
      if (filters.sortBy === 'email') return 'email';
      return 'newest';
    };

    const startFrom = ((pagination.page - 1) * pagination.limit) + 1;
    const endAt = Math.min(pagination.page * pagination.limit, pagination.total);

    const activeFilterChips = [];
    if (filters.contactType !== 'ALL') {
      activeFilterChips.push({ label: `Type: ${filters.contactType}`, clear: () => setFilters(prev => ({ ...prev, contactType: 'ALL' })) });
    }
    if (filters.status) {
      activeFilterChips.push({ label: `Status: ${filters.status}`, clear: () => setFilters(prev => ({ ...prev, status: '' })) });
    }
    if (filters.tags.length > 0) {
      activeFilterChips.push({ label: `Tags: ${filters.tags.join(', ')}`, clear: () => setFilters(prev => ({ ...prev, tags: [] })) });
    }
    if (filters.segmentId) {
      const seg = allSegments.find(s => s.id === filters.segmentId);
      activeFilterChips.push({ label: `Segment: ${seg ? seg.name : filters.segmentId}`, clear: () => setFilters(prev => ({ ...prev, segmentId: '' })) });
    }
    if (filters.source) {
      activeFilterChips.push({ label: `Source: ${filters.source}`, clear: () => setFilters(prev => ({ ...prev, source: '' })) });
    }
    if (filters.startDate) {
      activeFilterChips.push({ label: `From: ${filters.startDate}`, clear: () => setFilters(prev => ({ ...prev, startDate: '' })) });
    }
    if (filters.endDate) {
      activeFilterChips.push({ label: `To: ${filters.endDate}`, clear: () => setFilters(prev => ({ ...prev, endDate: '' })) });
    }

    return (
      <div className="mkt-con-list-view">
        <div className="mkt-con-list-header">
          <div className="mkt-con-list-header-left">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setActiveView('overview')}>
              <ArrowLeft size={16} />
              Back to Overview
            </button>
            <h2 className="mkt-con-title">
              All Contacts
              <span className="mkt-con-count-badge">{pagination.total.toLocaleString()}</span>
            </h2>
          </div>
          <div className="mkt-con-list-header-actions">
            <button className="mkt-con-btn mkt-con-btn-outline" onClick={() => setShowExportModal(true)}>
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        <div className="mkt-con-search-row">
          <div className="mkt-con-search-input">
            <Search size={18} className="mkt-con-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchQuery && (
              <button className="mkt-con-search-clear" onClick={() => { setSearchQuery(''); setPagination(prev => ({ ...prev, page: 1 })); }}>
                <X size={14} />
              </button>
            )}
          </div>
          <div className="mkt-con-tabs-row">
          <div className="mkt-con-crm-tabs">
            {[
              { key: 'ALL', label: 'All' },
              { key: 'CONTACTS', label: 'Contacts' },
              { key: 'LEADS', label: 'Leads' }
            ].map(tab => (
              <button
                key={tab.key}
                className={`mkt-con-crm-tab ${crmTab === tab.key ? 'active' : ''}`}
                onClick={() => {
                  setCrmTab(tab.key);
                  if (tab.key === 'ALL') {
                    setFilters(prev => ({ ...prev, contactType: 'ALL', status: '' }));
                  } else if (tab.key === 'CONTACTS') {
                    setFilters(prev => ({ ...prev, contactType: 'MERCHANT', status: '' }));
                  } else if (tab.key === 'LEADS') {
                    setFilters(prev => ({ ...prev, contactType: 'LEADS', status: '' }));
                  }
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mkt-con-type-tabs">
            {(crmTab === 'ALL' ? [
              { key: 'ALL', label: 'All', count: typeCounts.all },
              { key: 'MERCHANT', label: 'Merchants', count: typeCounts.merchants },
              { key: 'CUSTOMER', label: 'Customers', count: typeCounts.customers },
              { key: 'LEAD', label: 'Leads', count: typeCounts.leads },
              { key: 'WAITLIST', label: 'Waitlist', count: typeCounts.waitlist }
            ] : crmTab === 'CONTACTS' ? [
              { key: 'MERCHANT', label: 'Merchants', count: typeCounts.merchants },
              { key: 'CUSTOMER', label: 'Customers', count: typeCounts.customers }
            ] : [
              { key: 'LEADS', label: 'All', count: (typeCounts.leads || 0) + (typeCounts.waitlist || 0) },
              { key: 'LEAD', label: 'Website Leads', count: typeCounts.leads },
              { key: 'WAITLIST', label: 'Waitlist', count: typeCounts.waitlist }
            ]).map(tab => (
              <button
                key={tab.key}
                className={`mkt-con-type-tab ${filters.contactType === tab.key ? 'active' : ''}`}
                onClick={() => {
                  setFilters(prev => ({ ...prev, contactType: tab.key, status: '' }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                {tab.label}
                <span className="mkt-con-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>
          </div>
        </div>

        <div className="mkt-con-filter-toggle-row">
          <button
            className={`mkt-con-btn mkt-con-btn-outline ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="mkt-con-filter-badge">{activeFilterCount}</span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button className="mkt-con-btn mkt-con-btn-ghost mkt-con-btn-sm" onClick={clearFilters}>
              <X size={14} />
              Clear All Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mkt-con-filter-panel">
            <div className="mkt-con-filter-grid">
              <div className="mkt-con-filter-field">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  {getStatusOptions(filters.contactType === 'ALL' ? '' : filters.contactType).map(s => (
                    <option key={s} value={s.toUpperCase().replace(/ /g, '_')}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="mkt-con-filter-field">
                <label>Source</label>
                <select
                  value={filters.source}
                  onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                >
                  <option value="">All Sources</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="contact_form">Contact Form</option>
                  <option value="exit_popup">Exit Popup</option>
                  <option value="platform">Platform</option>
                  <option value="storefront">Storefront</option>
                  <option value="waitlist">Waitlist</option>
                </select>
              </div>

              <div className="mkt-con-filter-field">
                <label>Segment</label>
                <select
                  value={filters.segmentId}
                  onChange={(e) => setFilters(prev => ({ ...prev, segmentId: e.target.value }))}
                >
                  <option value="">All Segments</option>
                  {allSegments.map(seg => (
                    <option key={seg.id} value={seg.id}>{seg.name}</option>
                  ))}
                </select>
              </div>

              <div className="mkt-con-filter-field">
                <label>Date From</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>

              <div className="mkt-con-filter-field">
                <label>Date To</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>

              <div className="mkt-con-filter-field">
                <label>Sort</label>
                <select value={getSortValue()} onChange={(e) => handleSortChange(e.target.value)}>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name">Name A-Z</option>
                  <option value="email">Email A-Z</option>
                </select>
              </div>
            </div>

            <div className="mkt-con-filter-tags-section">
              <label>Filter by Tags</label>
              <div className="mkt-con-filter-tag-input-wrap">
                <input
                  type="text"
                  placeholder="Type to search tags..."
                  value={filterTagInput}
                  onChange={(e) => handleFilterTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filterTagInput.trim()) {
                      addFilterTag(filterTagInput.trim());
                    }
                  }}
                />
                {filterTagSuggestions.length > 0 && (
                  <div className="mkt-con-tag-suggestions">
                    {filterTagSuggestions.map((t, idx) => (
                      <button
                        key={idx}
                        className="mkt-con-tag-suggestion-item"
                        onClick={() => addFilterTag(t.tag || t.name || '')}
                      >
                        {t.tag || t.name || ''}
                        <small>({t.count || 0})</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {filters.tags.length > 0 && (
                <div className="mkt-con-filter-selected-tags">
                  {filters.tags.map(tag => (
                    <span key={tag} className="mkt-con-tag-chip">
                      {tag}
                      <button onClick={() => removeFilterTag(tag)}><X size={12} /></button>
                    </span>
                  ))}
                  {filters.tags.length >= 2 && (
                    <div className="mkt-con-tag-logic-toggle">
                      <span>Match:</span>
                      <button
                        className={`mkt-con-logic-btn ${filters.tagLogic === 'OR' ? 'active' : ''}`}
                        onClick={() => setFilters(prev => ({ ...prev, tagLogic: 'OR' }))}
                      >
                        OR
                      </button>
                      <button
                        className={`mkt-con-logic-btn ${filters.tagLogic === 'AND' ? 'active' : ''}`}
                        onClick={() => setFilters(prev => ({ ...prev, tagLogic: 'AND' }))}
                      >
                        AND
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeFilterChips.length > 0 && (
          <div className="mkt-con-active-filters">
            {activeFilterChips.map((chip, idx) => (
              <span key={idx} className="mkt-con-filter-chip">
                {chip.label}
                <button onClick={chip.clear}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}

        {selectedContacts.length > 0 && (
          <div className="mkt-con-bulk-bar">
            <span className="mkt-con-bulk-count">{selectedContacts.length} contacts selected</span>
            <div className="mkt-con-bulk-actions">
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => { setBulkAction('ADD_TAGS'); setShowBulkTagModal(true); }}
              >
                <Tag size={14} />
                Add Tags
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => { setBulkAction('REMOVE_TAGS'); setShowBulkTagModal(true); }}
              >
                <X size={14} />
                Remove Tags
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => setShowSegmentModal(true)}
              >
                <Target size={14} />
                Add to Segment
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => handleSendCampaign(selectedContacts)}
              >
                <Send size={14} />
                Send Campaign
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => setShowExportModal(true)}
              >
                <Download size={14} />
                Export CSV
              </button>
              {selectedContacts.some(c => c.contactType === 'LEAD' || c.contactType === 'WAITLIST') && (
                <button
                  className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                  onClick={() => setShowBulkStatusModal(true)}
                >
                  <Activity size={14} />
                  Update Status
                </button>
              )}
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 size={14} />
                Delete
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-ghost"
                onClick={() => setSelectedContacts([])}
              >
                <X size={14} />
                Clear
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mkt-con-loading">
            <div className="mkt-con-spinner" />
            <p>Loading contacts...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="mkt-con-empty-state">
            <Users size={48} className="mkt-con-empty-icon" />
            <h3>No contacts found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <>
            <div className="mkt-con-table-wrap">
              <table className="mkt-con-table">
                <thead>
                  <tr>
                    <th className="mkt-con-th-check">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={selectAllOnPage}
                      />
                    </th>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Tags</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => {
                    const isSelected = selectedContacts.some(
                      sc => sc.id === contact.id && sc.contactType === contact.contactType
                    );
                    return (
                      <tr
                        key={`${contact.contactType}-${contact.id}`}
                        className={isSelected ? 'mkt-con-row-selected' : ''}
                      >
                        <td className="mkt-con-td-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleContactSelection(contact.id, contact.contactType)}
                          />
                        </td>
                        <td>
                          <div className="mkt-con-contact-cell">
                            <div className="mkt-con-avatar" style={{ background: avatarColor(contact.contactType) }}>
                              {getInitials(contact.name)}
                            </div>
                            <div className="mkt-con-contact-info">
                              <strong>{contact.name || 'Unknown'}</strong>
                              <small>{contact.email}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`mkt-con-type-badge mkt-con-type-${(contact.contactType || '').toLowerCase()}`}>
                            {contact.contactType}
                          </span>
                        </td>
                        <td>
                          <div className="mkt-con-tags-cell">
                            {(contact.tags || []).slice(0, 3).map(tag => (
                              <span key={tag} className="mkt-con-tag-chip">{tag}</span>
                            ))}
                            {(contact.tags || []).length > 3 && (
                              <span className="mkt-con-tag-more">+{contact.tags.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`mkt-con-status-badge mkt-con-status-${(contact.status || '').toLowerCase().replace(/_/g, '-')}`}
                            style={{ backgroundColor: `${getStatusColor(contact.status)}20`, color: getStatusColor(contact.status), borderColor: `${getStatusColor(contact.status)}30` }}
                          >
                            {LEAD_STATUS_LABELS[contact.status] || (contact.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>{contact.source || '\u2014'}</td>
                        <td>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '\u2014'}</td>
                        <td>
                          <button
                            className="mkt-con-btn-icon"
                            onClick={() => loadContactDetail(contact.id, contact.contactType)}
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="mkt-con-pagination">
                <span className="mkt-con-pagination-info">
                  Showing {startFrom}-{endAt} of {pagination.total.toLocaleString()} contacts
                </span>
                <div className="mkt-con-pagination-controls">
                  <button
                    className="mkt-con-pagination-btn"
                    onClick={() => goToPage(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  {getPageNumbers().map(num => (
                    <button
                      key={num}
                      className={`mkt-con-pagination-num ${num === pagination.page ? 'active' : ''}`}
                      onClick={() => goToPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="mkt-con-pagination-btn"
                    onClick={() => goToPage(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ─── VIEW: Contact Detail ─────────────────────────────────────────

  const ContactDetail = () => {
    const [detailTagInput, setDetailTagInput] = useState('');
    const [detailTagSuggestions, setDetailTagSuggestions] = useState([]);

    if (!contactDetail) {
      return (
        <div className="mkt-con-loading">
          <div className="mkt-con-spinner" />
          <p>Loading contact details...</p>
        </div>
      );
    }

    const contact = contactDetail;
    const fullRecord = contact.fullRecord || {};

    const handleDetailTagInput = (value) => {
      setDetailTagInput(value);
      if (value.trim()) {
        const currentTags = contact.tags || [];
        const suggestions = allTags.filter(t =>
          (t.tag || t.name || '').toLowerCase().includes(value.toLowerCase()) &&
          !currentTags.includes(t.tag || t.name || '')
        );
        setDetailTagSuggestions(suggestions);
      } else {
        setDetailTagSuggestions([]);
      }
    };

    const addDetailTag = async (tagName) => {
      try {
        const response = await apiWithAccessControl('/admin/contacts/bulk-action', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contactIds: [{ id: contact.id, contactType: contact.contactType }],
            action: 'ADD_TAGS',
            payload: { tags: [tagName] }
          })
        });
        const data = await response.json();
        if (data.success) {
          showStatus('success', `Tag "${tagName}" added`);
          setShowAddTagInput(false);
          setDetailTagInput('');
          setDetailTagSuggestions([]);
          loadContactDetail(contact.id, contact.contactType);
        } else {
          showStatus('error', data.message || 'Failed to add tag');
        }
      } catch (err) {
        showStatus('error', 'Error adding tag');
      }
    };

    const removeDetailTag = async (tagName) => {
      try {
        const response = await apiWithAccessControl('/admin/contacts/bulk-action', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contactIds: [{ id: contact.id, contactType: contact.contactType }],
            action: 'REMOVE_TAGS',
            payload: { tags: [tagName] }
          })
        });
        const data = await response.json();
        if (data.success) {
          showStatus('success', `Tag "${tagName}" removed`);
          loadContactDetail(contact.id, contact.contactType);
        } else {
          showStatus('error', data.message || 'Failed to remove tag');
        }
      } catch (err) {
        showStatus('error', 'Error removing tag');
      }
    };

    const addToSegmentFromDetail = () => {
      setSelectedContacts([{ id: contact.id, contactType: contact.contactType }]);
      setShowSegmentModal(true);
    };

    return (
      <div className="mkt-con-detail-view">
        <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setActiveView('list')}>
          <ArrowLeft size={16} />
          Back to Contacts
        </button>

        <div className="mkt-con-detail-header">
          <div className="mkt-con-detail-header-left">
            <div className="mkt-con-avatar mkt-con-avatar-lg" style={{ background: avatarColor(contact.contactType) }}>
              {getInitials(contact.name)}
            </div>
            <div className="mkt-con-detail-header-info">
              <h1>{contact.name || 'Unknown Contact'}</h1>
              <p className="mkt-con-detail-email">
                <Mail size={14} />
                {contact.email}
              </p>
              {contact.phone && (
                <p className="mkt-con-detail-phone">
                  <Phone size={14} />
                  {contact.phone}
                </p>
              )}
            </div>
          </div>
          <div className="mkt-con-detail-header-right">
            <span className={`mkt-con-type-badge mkt-con-type-${(contact.contactType || '').toLowerCase()}`}>
              {contact.contactType}
            </span>
            <span
              className={`mkt-con-status-badge mkt-con-status-${(contact.status || '').toLowerCase().replace(/_/g, '-')}`}
              style={{ backgroundColor: `${getStatusColor(contact.status)}20`, color: getStatusColor(contact.status), borderColor: `${getStatusColor(contact.status)}30` }}
            >
              {LEAD_STATUS_LABELS[contact.status] || (contact.status || '').replace(/_/g, ' ')}
            </span>
            {contact.contactType === 'LEAD' && (
              <div className="mkt-con-lead-status-dropdown">
                <label className="mkt-con-lead-status-label">Lead Status:</label>
                <select
                  className="mkt-con-lead-status-select"
                  value={contact.status || ''}
                  onChange={(e) => updateLeadStatus(contact.id, contact.contactType, e.target.value)}
                >
                  <option value="">-- Select Status --</option>
                  {LEAD_STATUSES.map(s => (
                    <option key={s} value={s}>{LEAD_STATUS_LABELS[s] || s}</option>
                  ))}
                </select>
              </div>
            )}
            {contact.contactType === 'WAITLIST' && (
              <div className="mkt-con-lead-status-dropdown">
                <label className="mkt-con-lead-status-label">Waitlist Status:</label>
                <select
                  className="mkt-con-lead-status-select"
                  value={contact.status || ''}
                  onChange={(e) => updateLeadStatus(contact.id, contact.contactType, e.target.value)}
                >
                  <option value="">-- Select Status --</option>
                  {WAITLIST_STATUSES.map(s => (
                    <option key={s} value={s}>{LEAD_STATUS_LABELS[s] || s}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mkt-con-detail-quick-actions">
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => syncContactToCrm(contact)}
                disabled={crmSyncingId === `${contact.contactType}-${contact.id}`}
              >
                <Zap size={14} />
                {crmSyncingId === `${contact.contactType}-${contact.id}` ? 'Syncing...' : 'Sync to CRM'}
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={() => setShowAddTagInput(!showAddTagInput)}
              >
                <Tag size={14} />
                Add Tag
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline"
                onClick={addToSegmentFromDetail}
              >
                <Target size={14} />
                Add to Segment
              </button>
              <button
                className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-primary"
                onClick={() => handleSendCampaign([{ id: contact.id, contactType: contact.contactType }])}
              >
                <Send size={14} />
                Send Campaign
              </button>
            </div>
          </div>
        </div>

        <div className="mkt-con-detail-grid">
          <div className="mkt-con-detail-card">
            <h3 className="mkt-con-detail-card-title">Contact Information</h3>
            <div className="mkt-con-detail-fields">
              <div className="mkt-con-detail-field">
                <label>Email</label>
                <span>{contact.email || '\u2014'}</span>
              </div>
              <div className="mkt-con-detail-field">
                <label>Phone</label>
                <span>{contact.phone || '\u2014'}</span>
              </div>
              <div className="mkt-con-detail-field">
                <label>Source</label>
                <span>{contact.source || '\u2014'}</span>
              </div>
              <div className="mkt-con-detail-field">
                <label>Joined</label>
                <span>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '\u2014'}</span>
              </div>
            </div>
          </div>

          <div className="mkt-con-detail-card">
            <h3 className="mkt-con-detail-card-title">
              {contact.contactType === 'MERCHANT' && 'Merchant Details'}
              {contact.contactType === 'CUSTOMER' && 'Customer Details'}
              {contact.contactType === 'LEAD' && 'Lead Details'}
              {contact.contactType === 'WAITLIST' && 'Waitlist Details'}
            </h3>
            <div className="mkt-con-detail-fields">
              {contact.contactType === 'MERCHANT' && (
                <>
                  <div className="mkt-con-detail-field">
                    <label>Tier</label>
                    <span>{fullRecord.tier || fullRecord.subscriptionTier || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Email Verified</label>
                    <span>{fullRecord.emailVerified ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Last Login</label>
                    <span>{fullRecord.lastLogin ? new Date(fullRecord.lastLogin).toLocaleString() : '\u2014'}</span>
                  </div>
                  {fullRecord.store && (
                    <div className="mkt-con-detail-field">
                      <label>Store Name</label>
                      <span>{fullRecord.store.name || fullRecord.store.storeName || '\u2014'}</span>
                    </div>
                  )}
                </>
              )}
              {contact.contactType === 'CUSTOMER' && (
                <>
                  <div className="mkt-con-detail-field">
                    <label>Belongs to Merchant</label>
                    <span>{fullRecord.merchant?.name || fullRecord.merchant?.businessName || fullRecord.merchantId || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Loyalty Points</label>
                    <span>{fullRecord.loyaltyPoints != null ? fullRecord.loyaltyPoints : '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Email Verified</label>
                    <span>{fullRecord.emailVerified ? 'Yes' : 'No'}</span>
                  </div>
                </>
              )}
              {contact.contactType === 'LEAD' && (
                <>
                  <div className="mkt-con-detail-field">
                    <label>Lead Type</label>
                    <span>{fullRecord.leadType || fullRecord.type || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Subject</label>
                    <span>{fullRecord.subject || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Message</label>
                    <span className="mkt-con-detail-message">{fullRecord.message || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Source</label>
                    <span>{fullRecord.utmSource || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Medium</label>
                    <span>{fullRecord.utmMedium || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Campaign</label>
                    <span>{fullRecord.utmCampaign || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Referrer</label>
                    <span>{fullRecord.referrer || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Admin Notified</label>
                    <span>{fullRecord.adminNotified ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Auto-reply Sent</label>
                    <span>{fullRecord.autoReplySent ? 'Yes' : 'No'}</span>
                  </div>
                </>
              )}
              {contact.contactType === 'WAITLIST' && (
                <>
                  <div className="mkt-con-detail-field">
                    <label>Business Name</label>
                    <span>{fullRecord.businessName || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Business Type</label>
                    <span>{fullRecord.businessType || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Country</label>
                    <span>{fullRecord.country || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Emails Sent</label>
                    <span>{fullRecord.emailsSent != null ? fullRecord.emailsSent : '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Email Opens</label>
                    <span>{fullRecord.emailOpens != null ? fullRecord.emailOpens : '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Email Clicks</label>
                    <span>{fullRecord.emailClicks != null ? fullRecord.emailClicks : '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Source</label>
                    <span>{fullRecord.utmSource || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Medium</label>
                    <span>{fullRecord.utmMedium || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>UTM Campaign</label>
                    <span>{fullRecord.utmCampaign || '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Invited At</label>
                    <span>{fullRecord.invitedAt ? new Date(fullRecord.invitedAt).toLocaleString() : '\u2014'}</span>
                  </div>
                  <div className="mkt-con-detail-field">
                    <label>Converted At</label>
                    <span>{fullRecord.convertedAt ? new Date(fullRecord.convertedAt).toLocaleString() : '\u2014'}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mkt-con-detail-grid">
          <div className="mkt-con-detail-card">
            <h3 className="mkt-con-detail-card-title">
              <Tag size={16} />
              Tags
            </h3>
            <div className="mkt-con-detail-tags">
              {(contact.tags || []).length > 0 ? (
                <div className="mkt-con-tags-list">
                  {contact.tags.map(tag => (
                    <span key={tag} className="mkt-con-tag-chip">
                      {tag}
                      <button onClick={() => removeDetailTag(tag)} title="Remove tag">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mkt-con-empty-text">No tags assigned</p>
              )}

              {showAddTagInput ? (
                <div className="mkt-con-inline-tag-form">
                  <div className="mkt-con-inline-tag-input-wrap">
                    <input
                      type="text"
                      placeholder="Type a tag name..."
                      value={detailTagInput}
                      onChange={(e) => handleDetailTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && detailTagInput.trim()) {
                          addDetailTag(detailTagInput.trim());
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-primary"
                      onClick={() => {
                        if (detailTagInput.trim()) addDetailTag(detailTagInput.trim());
                      }}
                    >
                      <Plus size={14} />
                      Add
                    </button>
                    <button
                      className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-ghost"
                      onClick={() => {
                        setShowAddTagInput(false);
                        setDetailTagInput('');
                        setDetailTagSuggestions([]);
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {detailTagSuggestions.length > 0 && (
                    <div className="mkt-con-tag-suggestions">
                      {detailTagSuggestions.map((t, idx) => (
                        <button
                          key={idx}
                          className="mkt-con-tag-suggestion-item"
                          onClick={() => addDetailTag(t.tag || t.name || '')}
                        >
                          {t.tag || t.name || ''}
                          <small>({t.count || 0})</small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  className="mkt-con-btn mkt-con-btn-sm mkt-con-btn-outline mkt-con-mt-sm"
                  onClick={() => setShowAddTagInput(true)}
                >
                  <Plus size={14} />
                  Add Tag
                </button>
              )}
            </div>
          </div>

          <div className="mkt-con-detail-card">
            <h3 className="mkt-con-detail-card-title">
              <Target size={16} />
              Segments
            </h3>
            {contactSegments.length > 0 ? (
              <div className="mkt-con-segments-list">
                {contactSegments.map(seg => (
                  <div key={seg.id} className="mkt-con-segment-item">
                    <div className="mkt-con-segment-info">
                      <strong>{seg.name}</strong>
                      <small>{seg.type} - {seg.recipientCount} recipients</small>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mkt-con-empty-text">Not a member of any segments</p>
            )}
          </div>
        </div>

        <div className="mkt-con-detail-tabs">
          <div className="mkt-con-tab-nav">
            <button
              className={`mkt-con-tab-btn ${detailTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => setDetailTab('campaigns')}
            >
              <Send size={14} />
              Campaigns
            </button>
            <button
              className={`mkt-con-tab-btn ${detailTab === 'automations' ? 'active' : ''}`}
              onClick={() => setDetailTab('automations')}
            >
              <Zap size={14} />
              Automations
            </button>
            <button
              className={`mkt-con-tab-btn ${detailTab === 'activity' ? 'active' : ''}`}
              onClick={() => setDetailTab('activity')}
            >
              <Activity size={14} />
              Activity Log
            </button>
          </div>

          <div className="mkt-con-tab-content">
            {detailTab === 'campaigns' && (
              <div className="mkt-con-campaigns-tab">
                {contactCampaigns.length > 0 ? (
                  <div className="mkt-con-table-wrap">
                    <table className="mkt-con-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>Sent</th>
                          <th>Opened</th>
                          <th>Clicked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactCampaigns.map(c => (
                          <tr key={c.id}>
                            <td>
                              <strong>{c.name}</strong>
                              <br />
                              <small className="mkt-con-text-muted">{c.subject}</small>
                            </td>
                            <td>{c.sentAt ? new Date(c.sentAt).toLocaleDateString() : '\u2014'}</td>
                            <td>
                              {c.opened ? (
                                <span className="mkt-con-engagement-yes">
                                  <CheckCircle size={16} className="mkt-con-icon-success" />
                                  {c.openedAt && <small>{new Date(c.openedAt).toLocaleDateString()}</small>}
                                </span>
                              ) : (
                                <XCircle size={16} className="mkt-con-icon-muted" />
                              )}
                            </td>
                            <td>
                              {c.clicked ? (
                                <span className="mkt-con-engagement-yes">
                                  <CheckCircle size={16} className="mkt-con-icon-success" />
                                  {c.clickedAt && <small>{new Date(c.clickedAt).toLocaleDateString()}</small>}
                                </span>
                              ) : (
                                <XCircle size={16} className="mkt-con-icon-muted" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mkt-con-tab-empty">
                    <Send size={32} className="mkt-con-icon-muted" />
                    <p>No campaign history</p>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'automations' && (
              <div className="mkt-con-automations-tab">
                {contactAutomations.length > 0 ? (
                  <div className="mkt-con-table-wrap">
                    <table className="mkt-con-table">
                      <thead>
                        <tr>
                          <th>Rule Name</th>
                          <th>Trigger</th>
                          <th>Status</th>
                          <th>Executed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactAutomations.map(a => (
                          <tr key={a.id}>
                            <td><strong>{a.name}</strong></td>
                            <td>{a.triggerType || '\u2014'}</td>
                            <td>
                              <span className={`mkt-con-status-badge mkt-con-status-${(a.status || '').toLowerCase()}`}>
                                {a.status}
                              </span>
                            </td>
                            <td>{a.executedAt ? new Date(a.executedAt).toLocaleString() : '\u2014'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mkt-con-tab-empty">
                    <Zap size={32} className="mkt-con-icon-muted" />
                    <p>No automation history</p>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'activity' && (
              <div className="mkt-con-activity-tab">
                {contactActivity.length > 0 ? (
                  <div className="mkt-con-timeline">
                    {contactActivity.map((item, idx) => (
                      <div key={idx} className="mkt-con-timeline-item">
                        <div className={`mkt-con-timeline-icon mkt-con-timeline-${item.type}`}>
                          {item.type === 'campaign_sent' && <Send size={14} />}
                          {item.type === 'campaign_opened' && <Eye size={14} />}
                          {item.type === 'campaign_clicked' && <MousePointer size={14} />}
                          {item.type === 'automation_executed' && <Zap size={14} />}
                          {item.type === 'preference_changed' && <Settings size={14} />}
                          {item.type === 'unsubscribed' && <XCircle size={14} />}
                        </div>
                        <div className="mkt-con-timeline-content">
                          <p>{item.description}</p>
                          <small className="mkt-con-text-muted">{new Date(item.timestamp).toLocaleString()}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mkt-con-tab-empty">
                    <Activity size={32} className="mkt-con-icon-muted" />
                    <p>No activity recorded</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL: Bulk Tag ──────────────────────────────────────────────

  const BulkTagModal = () => {
    const [modalTags, setModalTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [applying, setApplying] = useState(false);

    const handleTagInput = (value) => {
      setTagInput(value);
      if (value.trim()) {
        const filtered = allTags.filter(t =>
          (t.tag || t.name || '').toLowerCase().includes(value.toLowerCase()) &&
          !modalTags.includes(t.tag || t.name || '')
        );
        setSuggestions(filtered);
      } else {
        setSuggestions([]);
      }
    };

    const addTag = (tagName) => {
      if (!modalTags.includes(tagName)) {
        setModalTags(prev => [...prev, tagName]);
      }
      setTagInput('');
      setSuggestions([]);
    };

    const removeTag = (tagName) => {
      setModalTags(prev => prev.filter(t => t !== tagName));
    };

    const handleApply = async () => {
      if (modalTags.length === 0) {
        showStatus('error', 'Please select at least one tag');
        return;
      }
      setApplying(true);
      await executeBulkAction(bulkAction, { tags: modalTags });
      setApplying(false);
    };

    return (
      <div className="mkt-con-modal-overlay" onClick={() => setShowBulkTagModal(false)}>
        <div className="mkt-con-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-con-modal-header">
            <h3>{bulkAction === 'ADD_TAGS' ? 'Add' : 'Remove'} Tags &mdash; {selectedContacts.length} contacts</h3>
            <button className="mkt-con-modal-close" onClick={() => setShowBulkTagModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="mkt-con-modal-body">
            <div className="mkt-con-modal-tag-input-wrap">
              <input
                type="text"
                placeholder="Type to search or add a tag..."
                value={tagInput}
                onChange={(e) => handleTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    addTag(tagInput.trim());
                  }
                }}
                autoFocus
              />
              {suggestions.length > 0 && (
                <div className="mkt-con-tag-suggestions">
                  {suggestions.map((t, idx) => (
                    <button
                      key={idx}
                      className="mkt-con-tag-suggestion-item"
                      onClick={() => addTag(t.tag || t.name || '')}
                    >
                      {t.tag || t.name || ''}
                      <small>({t.count || 0})</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {modalTags.length > 0 && (
              <div className="mkt-con-modal-selected-tags">
                {modalTags.map(tag => (
                  <span key={tag} className="mkt-con-tag-chip">
                    {tag}
                    <button onClick={() => removeTag(tag)}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="mkt-con-modal-footer">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setShowBulkTagModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-con-btn mkt-con-btn-primary"
              onClick={handleApply}
              disabled={modalTags.length === 0 || applying}
            >
              {applying ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL: Add to Segment ────────────────────────────────────────

  const AddToSegmentModal = () => {
    const [selectedSegmentId, setSelectedSegmentId] = useState('');
    const [applying, setApplying] = useState(false);

    const handleAdd = async () => {
      if (!selectedSegmentId) {
        showStatus('error', 'Please select a segment');
        return;
      }
      setApplying(true);
      await executeBulkAction('ADD_TO_SEGMENT', { segmentId: selectedSegmentId });
      setApplying(false);
    };

    return (
      <div className="mkt-con-modal-overlay" onClick={() => setShowSegmentModal(false)}>
        <div className="mkt-con-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-con-modal-header">
            <h3>Add to Segment &mdash; {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''}</h3>
            <button className="mkt-con-modal-close" onClick={() => setShowSegmentModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="mkt-con-modal-body">
            <p className="mkt-con-modal-note">
              <AlertTriangle size={14} />
              Segments currently support merchant contacts only. Non-merchant contacts will be skipped.
            </p>
            <div className="mkt-con-modal-field">
              <label>Select Segment</label>
              <select value={selectedSegmentId} onChange={(e) => setSelectedSegmentId(e.target.value)}>
                <option value="">Choose a segment...</option>
                {allSegments.map(seg => (
                  <option key={seg.id} value={seg.id}>
                    {seg.name} ({seg.recipientCount} recipients)
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mkt-con-modal-footer">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setShowSegmentModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-con-btn mkt-con-btn-primary"
              onClick={handleAdd}
              disabled={!selectedSegmentId || applying}
            >
              {applying ? 'Adding...' : 'Add to Segment'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL: Export ────────────────────────────────────────────────

  const ExportModal = () => {
    const [exportScope, setExportScope] = useState(selectedContacts.length > 0 ? 'selected' : 'all');
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
      setExporting(true);
      await exportContacts(exportScope);
      setExporting(false);
    };

    return (
      <div className="mkt-con-modal-overlay" onClick={() => setShowExportModal(false)}>
        <div className="mkt-con-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-con-modal-header">
            <h3>Export Contacts</h3>
            <button className="mkt-con-modal-close" onClick={() => setShowExportModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="mkt-con-modal-body">
            <div className="mkt-con-export-options">
              {selectedContacts.length > 0 && (
                <label className="mkt-con-radio-option">
                  <input
                    type="radio"
                    name="exportScope"
                    value="selected"
                    checked={exportScope === 'selected'}
                    onChange={() => setExportScope('selected')}
                  />
                  <span>Export selected ({selectedContacts.length} contacts)</span>
                </label>
              )}
              <label className="mkt-con-radio-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={() => setExportScope('all')}
                />
                <span>Export all matching filters ({pagination.total.toLocaleString()} contacts)</span>
              </label>
            </div>
          </div>
          <div className="mkt-con-modal-footer">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setShowExportModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-con-btn mkt-con-btn-primary"
              onClick={handleExport}
              disabled={exporting}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Download CSV'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL: Delete Confirm ────────────────────────────────────────

  const DeleteConfirmModal = () => {
    const [deleting, setDeleting] = useState(false);

    const merchantCount = selectedContacts.filter(c => c.contactType === 'MERCHANT').length;
    const customerCount = selectedContacts.filter(c => c.contactType === 'CUSTOMER').length;
    const leadCount = selectedContacts.filter(c => c.contactType === 'LEAD').length;
    const waitlistCount = selectedContacts.filter(c => c.contactType === 'WAITLIST').length;

    const handleDelete = async () => {
      setDeleting(true);
      await executeBulkAction('DELETE', {});
      setDeleting(false);
    };

    return (
      <div className="mkt-con-modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="mkt-con-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-con-modal-header">
            <h3>Delete {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}</h3>
            <button className="mkt-con-modal-close" onClick={() => setShowDeleteModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="mkt-con-modal-body">
            <div className="mkt-con-delete-warning">
              <AlertTriangle size={20} />
              <div>
                <strong>This action cannot be undone.</strong>
                <p>Merchants cannot be deleted from this page.</p>
              </div>
            </div>
            <div className="mkt-con-delete-breakdown">
              <p>Selected contacts breakdown:</p>
              <ul>
                {merchantCount > 0 && <li>Merchants: {merchantCount} (will be skipped)</li>}
                {customerCount > 0 && <li>Customers: {customerCount}</li>}
                {leadCount > 0 && <li>Leads: {leadCount}</li>}
                {waitlistCount > 0 && <li>Waitlist: {waitlistCount}</li>}
              </ul>
            </div>
          </div>
          <div className="mkt-con-modal-footer">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-con-btn mkt-con-btn-danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              <Trash2 size={16} />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL: Bulk Status Update ───────────────────────────────────

  const BulkStatusModal = () => {
    const [selectedStatus, setSelectedStatus] = useState('');
    const [updating, setUpdating] = useState(false);

    const leadCount = selectedContacts.filter(c => c.contactType === 'LEAD').length;
    const waitlistCount = selectedContacts.filter(c => c.contactType === 'WAITLIST').length;
    const otherCount = selectedContacts.filter(c => c.contactType !== 'LEAD' && c.contactType !== 'WAITLIST').length;

    // Determine which statuses to show based on selected contacts
    const hasLeads = leadCount > 0;
    const hasWaitlist = waitlistCount > 0;
    const hasMixed = hasLeads && hasWaitlist;

    // If mixed, show a category picker; otherwise show the relevant statuses
    const [statusCategory, setStatusCategory] = useState(hasLeads ? 'LEAD' : 'WAITLIST');
    const availableStatuses = statusCategory === 'LEAD' ? LEAD_STATUSES : WAITLIST_STATUSES;

    const handleUpdate = async () => {
      if (!selectedStatus) return;
      setUpdating(true);

      // Filter contacts to only the relevant type
      const relevantContacts = selectedContacts.filter(c => c.contactType === statusCategory);
      if (relevantContacts.length === 0) {
        showStatus('error', `No ${statusCategory === 'LEAD' ? 'lead' : 'waitlist'} contacts selected`);
        setUpdating(false);
        return;
      }

      try {
        const response = await apiWithAccessControl('/admin/contacts/bulk-action', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contactIds: relevantContacts,
            action: 'UPDATE_LEAD_STATUS',
            payload: { status: selectedStatus }
          })
        });
        const data = await response.json();
        if (data.success) {
          showStatus('success', data.message || `Status updated for ${data.succeeded || relevantContacts.length} contacts`);
          setShowBulkStatusModal(false);
          setSelectedContacts([]);
          if (activeView === 'list') loadContacts(pagination.page);
        } else {
          showStatus('error', data.message || 'Failed to update status');
        }
      } catch (err) {
        showStatus('error', 'Error updating status');
      } finally {
        setUpdating(false);
      }
    };

    return (
      <div className="mkt-con-modal-overlay" onClick={() => setShowBulkStatusModal(false)}>
        <div className="mkt-con-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-con-modal-header">
            <h3>Update Status</h3>
            <button className="mkt-con-modal-close" onClick={() => setShowBulkStatusModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="mkt-con-modal-body">
            <div style={{ marginBottom: 12, fontSize: 13, color: '#94a3b8' }}>
              {leadCount > 0 && <span>{leadCount} lead{leadCount !== 1 ? 's' : ''}</span>}
              {leadCount > 0 && waitlistCount > 0 && <span>, </span>}
              {waitlistCount > 0 && <span>{waitlistCount} waitlist contact{waitlistCount !== 1 ? 's' : ''}</span>}
              {otherCount > 0 && <span style={{ display: 'block', marginTop: 4, color: '#f59e0b' }}>
                {otherCount} merchant/customer contact{otherCount !== 1 ? 's' : ''} will be skipped (no lead status)
              </span>}
            </div>

            {hasMixed && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#e2e8f0' }}>
                  Apply to:
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className={`mkt-con-btn mkt-con-btn-sm ${statusCategory === 'LEAD' ? 'mkt-con-btn-primary' : 'mkt-con-btn-outline'}`}
                    onClick={() => { setStatusCategory('LEAD'); setSelectedStatus(''); }}
                    type="button"
                  >
                    Leads ({leadCount})
                  </button>
                  <button
                    className={`mkt-con-btn mkt-con-btn-sm ${statusCategory === 'WAITLIST' ? 'mkt-con-btn-primary' : 'mkt-con-btn-outline'}`}
                    onClick={() => { setStatusCategory('WAITLIST'); setSelectedStatus(''); }}
                    type="button"
                  >
                    Waitlist ({waitlistCount})
                  </button>
                </div>
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#e2e8f0' }}>
                {statusCategory === 'LEAD' ? 'Lead Status' : 'Waitlist Status'}:
              </label>
              <select
                className="mkt-con-lead-status-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
              >
                <option value="">-- Select Status --</option>
                {availableStatuses.map(s => (
                  <option key={s} value={s}>{LEAD_STATUS_LABELS[s] || s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mkt-con-modal-footer">
            <button className="mkt-con-btn mkt-con-btn-ghost" onClick={() => setShowBulkStatusModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-con-btn mkt-con-btn-primary"
              onClick={handleUpdate}
              disabled={!selectedStatus || updating}
            >
              <Activity size={16} />
              {updating ? 'Updating...' : `Update ${statusCategory === 'LEAD' ? 'Lead' : 'Waitlist'} Status`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────

  return (
    <div className="mkt-con-container">
      <StatusBanner />

      {loading && activeView !== 'list' && activeView !== 'detail' && (
        <div className="mkt-con-loading">
          <div className="mkt-con-spinner" />
          <p>Loading...</p>
        </div>
      )}

      {activeView === 'overview' && !loading && <ContactsOverview />}
      {activeView === 'list' && <ContactsList />}
      {activeView === 'detail' && <ContactDetail />}

      {showBulkTagModal && <BulkTagModal />}
      {showSegmentModal && <AddToSegmentModal />}
      {showExportModal && <ExportModal />}
      {showDeleteModal && <DeleteConfirmModal />}
      {showBulkStatusModal && <BulkStatusModal />}

      <SendCampaignModal
        isOpen={showSendCampaign}
        onClose={() => setShowSendCampaign(false)}
        selectedContacts={sendCampaignContacts}
        apiWithAccessControl={apiWithAccessControl}
        onSuccess={(msg) => {
          setShowSendCampaign(false);
          showStatus('success', msg || 'Campaign sent successfully');
        }}
      />
    </div>
  );
}

export default ContactsManagement;
